import {
  Col,
  Flex,
  LinkText,
  Row,
  Space,
  Tag,
  Text,
  TitledText,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import {
  type PinpointSheetType,
  getPinpointSceneAndFieldBySheetType,
} from '@/features/comment/utils/pinpoint';
import type { CostsModel } from '@/features/master-table/models';
import {
  BusinessFormLabelModifier,
  type FundAdditionalAttributesModel,
  FundNecessityTypeLabelModifier,
  type LegalBasisModel,
  OperationFormLabelModifier,
  type PlanModel,
  type PoliciesForProjectModel,
  type RelatedProjectModel,
} from '@/features/project/projects/_models/project';
import { PROJECT_BASE_DIR } from '@/features/project/review-sheet/utils/constants';
import { FALLBACK_TEXT, ITEM_SPLITTER, isPS } from '@/utils';
import {
  ProjectCategoryLabelModifier,
  SheetTypeLabelModifier,
  type projectCategoryEnum,
} from '@/utils/enum';
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { GroupTag } from '../GroupTag';
import { LegalBasisCard } from '../LegalBasisCard';
import { PlanCard } from '../PlanCard';
import { PolicyForProjectCard } from '../PolicyForProjectCard';

export const ProjectNumberPinpointItem: FC<{
  projectNumber: string | null;
  projectId: string;
  pinpointSheetType: PinpointSheetType;
}> = ({ projectNumber, projectId, pinpointSheetType }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.project-number',
        },
        rsSkippedReview: {
          path: { project: projectId },
          field: 'rs.skippedReview.project-number',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.project-number',
        },
        ksInvestment: {
          path: { project: projectId },
          field: 'ks.investment.project-number',
        },
        ksStandard: {
          path: { project: projectId },
          field: 'ks.standard.project-number',
        },
        ksLocalGovernment: {
          path: { project: projectId },
          field: 'ks.local-government.project-number',
        },
      })}
    >
      <TitledText title={t('project-number')} text={projectNumber} />
    </PinpointMarker>
  );
};

const _ProjectGroupPinpointItem: FC<{
  projectId: string;
  pinpointSheetType: PinpointSheetType;
}> = ({ projectId, pinpointSheetType }) => {
  if (isPS()) {
    return null;
  }
  return (
    <PinpointMarker
      key="project-group"
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.project-group',
        },
        rsSkippedReview: {
          path: { project: projectId },
          field: 'rs.skippedReview.project-group',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.project-group',
        },
        ksInvestment: {
          path: { project: projectId },
          field: 'ks.investment.project-group',
        },
        ksStandard: {
          path: { project: projectId },
          field: 'ks.standard.project-group',
        },
        ksLocalGovernment: {
          path: { project: projectId },
          field: 'ks.local-government.project-group',
        },
      })}
    >
      <GroupTag />
    </PinpointMarker>
  );
};

const _ProjectSheetTypePinpointItem: FC<{
  projectId: string;
  isSkipAdministrative?: boolean;
  pinpointSheetType: PinpointSheetType;
}> = ({ projectId, isSkipAdministrative, pinpointSheetType }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.sheet-type',
        },
        rsSkippedReview: {
          path: { project: projectId },
          field: 'rs.skippedReview.sheet-type',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.sheet-type',
        },
      })}
    >
      <Flex gap={12} align="center">
        <Text variant="footnote" text={t('sheet-type')} color="secondary" />
        <Text
          variant="footnote"
          text={
            SheetTypeLabelModifier[
              pinpointSheetType === 'RS_SKIPPED_REVIEW'
                ? 'RS'
                : pinpointSheetType
            ]
          }
        />
        {(pinpointSheetType === 'RS' ||
          pinpointSheetType === 'RS_SKIPPED_REVIEW') && (
          <Tag color={isSkipAdministrative ? 'green' : 'default'}>
            {isSkipAdministrative
              ? t('skip-administrative-reform-team-evaluation')
              : t('need-administrative-reform-team-evaluation')}
          </Tag>
        )}
      </Flex>
    </PinpointMarker>
  );
};

const _ProjectFiscalYearPinpointItem: FC<{
  projectId: string;
  fiscalYear: number;
  pinpointSheetType: PinpointSheetType;
}> = ({ projectId, fiscalYear, pinpointSheetType }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      key="fiscal-year"
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.fiscal-year',
        },
        rsSkippedReview: {
          path: { project: projectId },
          field: 'rs.skippedReview.fiscal-year',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.fiscal-year',
        },
      })}
    >
      <TitledText title={t('fiscal-year')} text={fiscalYear} />
    </PinpointMarker>
  );
};

const _ProjectCategoryPinpointItem: FC<{
  projectId: string;
  category: z.infer<typeof projectCategoryEnum> | null | undefined;
  pinpointSheetType: PinpointSheetType;
}> = ({ projectId, category, pinpointSheetType }) => {
  const { t } = useTranslation();
  if (!category) {
    return null;
  }
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.project-category',
        },
        rsSkippedReview: {
          path: { project: projectId },
          field: 'rs.skippedReview.project-category',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.project-category',
        },
      })}
    >
      <TitledText
        title={t('project-category')}
        text={ProjectCategoryLabelModifier[category]}
      />
    </PinpointMarker>
  );
};
const _ProjectPeriodPinpointItem: FC<{
  projectId: string;
  startYear: number | null;
  isUnknownStart: boolean;
  endYear: number | null;
  isNoScheduledEnd: boolean;
  pinpointSheetType: PinpointSheetType;
}> = ({
  projectId,
  startYear,
  isUnknownStart,
  endYear,
  isNoScheduledEnd,
  pinpointSheetType,
}) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      key="period"
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.period',
        },
        rsSkippedReview: {
          path: { project: projectId },
          field: 'rs.skippedReview.period',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.period',
        },
        ksInvestment: {
          path: { project: projectId },
          field: 'ks.investment.period',
        },
      })}
    >
      <TitledText
        key="period"
        title={t('period')}
        text={`${
          startYear ?? (isUnknownStart ? t('unknown-start') : t('blank'))
        } 〜 ${
          endYear ?? (isNoScheduledEnd ? t('no-scheduled-end') : t('blank'))
        }`}
      />
    </PinpointMarker>
  );
};

const _ProjectPolicyPinpointItem: FC<{
  projectId: string;
  policiesForProject: z.infer<typeof PoliciesForProjectModel>[];
  pinpointSheetType: PinpointSheetType;
}> = ({ projectId, policiesForProject, pinpointSheetType }) => {
  const { t } = useTranslation();
  return (
    <Space direction="vertical">
      <PinpointMarker
        key="policy"
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType,
          rs: {
            path: { project: projectId },
            field: 'rs.policy-title',
          },
          rsSkippedReview: {
            path: { project: projectId },
            field: 'rs.skippedReview.policy-title',
          },
        })}
      >
        <Text variant="body" color="secondary" text={t('policy')} />
      </PinpointMarker>
      {policiesForProject.length === 0 ? (
        <Text text="--" />
      ) : (
        <Row gutter={[24, 16]}>
          {policiesForProject.map((policyForProject) => {
            return (
              <Col span={12} key={policyForProject.policyId}>
                <PinpointMarker
                  key="policy"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType,
                    rs: {
                      path: {
                        project: projectId,
                        policy: policyForProject.policyId,
                      },
                      field: 'rs.policy',
                    },
                    rsSkippedReview: {
                      path: {
                        project: projectId,
                        policy: policyForProject.policyId,
                      },
                      field: 'rs.skippedReview.policy',
                    },
                  })}
                >
                  <PolicyForProjectCard policyForProject={policyForProject} />
                </PinpointMarker>
              </Col>
            );
          })}
        </Row>
      )}
    </Space>
  );
};
const _ProjectOldProjectNumberPinpointItem: FC<{
  projectId: string;
  oldProjectNumber: string | null | undefined;
  pinpointSheetType: PinpointSheetType;
}> = ({ projectId, oldProjectNumber, pinpointSheetType }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.old-project-number',
        },
        rsSkippedReview: {
          path: { project: projectId },
          field: 'rs.skippedReview.old-project-number',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.old-project-number',
        },
        ksInvestment: {
          path: { project: projectId },
          field: 'ks.investment.old-project-number',
        },
        ksStandard: {
          path: { project: projectId },
          field: 'ks.standard.old-project-number',
        },
        ksLocalGovernment: {
          path: { project: projectId },
          field: 'ks.local-government.old-project-number',
        },
      })}
    >
      <TitledText title={t('old-project-number')} text={oldProjectNumber} />
    </PinpointMarker>
  );
};
const _ProjectOrderNumberPinpointItem: FC<{
  projectId: string;
  sortOrder: number | null | undefined;
  pinpointSheetType: PinpointSheetType;
}> = ({ projectId, sortOrder, pinpointSheetType }) => {
  const { t } = useTranslation();
  if (isPS()) {
    return null;
  }
  return (
    <PinpointMarker
      key="project-order-number"
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.project-order-number',
        },
        rsSkippedReview: {
          path: { project: projectId },
          field: 'rs.skippedReview.project-order-number',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.project-order-number',
        },
      })}
    >
      <TitledText title={t('project-order-number')} text={sortOrder} />
    </PinpointMarker>
  );
};
const _ProjectRemarkPinpointItem: FC<{
  projectId: string;
  remark: string | null | undefined;
  pinpointSheetType: PinpointSheetType;
}> = ({ projectId, remark, pinpointSheetType }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.remark',
        },
        rsSkippedReview: {
          path: { project: projectId },
          field: 'rs.skippedReview.remark',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.remark',
        },
        ksInvestment: {
          path: { project: projectId },
          field: 'ks.investment.remark',
        },
        ksStandard: {
          path: { project: projectId },
          field: 'ks.standard.remark',
        },
        ksLocalGovernment: {
          path: { project: projectId },
          field: 'ks.local-government.remark',
        },
      })}
    >
      <TitledText title={t('remark')} text={remark} />
    </PinpointMarker>
  );
};

export const ProjectRelatedProjectsPinpointItem: FC<{
  projectId: string;
  relatedProjects: z.infer<typeof RelatedProjectModel>[];
  pinpointSheetType: PinpointSheetType;
}> = ({ projectId, relatedProjects, pinpointSheetType }) => {
  const { t } = useTranslation();

  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.related-projects',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.related-projects',
        },
        ksStandard: {
          path: { project: projectId },
          field: 'ks.standard.related-projects',
        },
      })}
    >
      <Space direction="vertical">
        <Text variant="body" color="secondary" text={t('related-projects')} />
        {relatedProjects.length === 0 ? (
          <Text text="--" />
        ) : (
          <Row>
            {relatedProjects.map((relatedProject) => {
              return (
                <Col
                  key={`${relatedProject.id}-${relatedProject.relationType}`}
                  span={24}
                >
                  <LinkText
                    text={`${relatedProject.name}`}
                    href={`${PROJECT_BASE_DIR}/${relatedProject.id}`}
                  />
                </Col>
              );
            })}
          </Row>
        )}
      </Space>
    </PinpointMarker>
  );
};

const _ProjectMainCostPinpointItem: FC<{
  projectId: string;
  cost: z.infer<typeof CostsModel>;
  pinpointSheetType: PinpointSheetType;
}> = ({ projectId, cost, pinpointSheetType }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.main-cost',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.main-cost',
        },
      })}
    >
      <TitledText
        title={t('main-cost')}
        text={cost.map((d) => d.name).join(ITEM_SPLITTER)}
      />
    </PinpointMarker>
  );
};

export const ProjectPurposePinpointItem: FC<{
  projectId: string;
  pinpointSheetType: PinpointSheetType;
  purpose: string | null | undefined;
}> = ({ projectId, purpose, pinpointSheetType }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.project-purpose',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.project-purpose',
        },
        ksStandard: {
          path: { project: projectId },
          field: 'ks.standard.project-purpose',
        },
      })}
    >
      <Space direction="vertical">
        <TitledText title={t('project-purpose')} text={purpose} />
      </Space>
    </PinpointMarker>
  );
};

export const ProjectTaskPinpointItem: FC<{
  projectId: string;
  pinpointSheetType: PinpointSheetType;
  task: string | null | undefined;
}> = ({ projectId, task, pinpointSheetType }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.project-task',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.project-task',
        },
        ksStandard: {
          path: { project: projectId },
          field: 'ks.standard.project-task',
        },
      })}
    >
      <Space direction="vertical" key="project-task">
        <TitledText title={t('project-task')} text={task} />
      </Space>
    </PinpointMarker>
  );
};

export const ProjectOverviewPinpointItem: FC<{
  projectId: string;
  pinpointSheetType: PinpointSheetType;
  overview: string | null | undefined;
  overviewUrl: string | null | undefined;
}> = ({
  projectId,
  pinpointSheetType,
  overview,
  overviewUrl: _overviewUrl,
}) => {
  const { t } = useTranslation();
  return (
    <Space direction="vertical" key="overview">
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType,
          rs: {
            path: { project: projectId },
            field: 'rs.overview',
          },
          ss: {
            path: { project: projectId },
            field: 'ss.overview',
          },
          ksStandard: {
            path: { project: projectId },
            field: 'ks.standard.overview',
          },
        })}
      >
        <TitledText title={t('project-overview')} text={overview} />
      </PinpointMarker>

      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType,
          rs: {
            path: { project: projectId },
            field: 'rs.overview-url',
          },
          ss: {
            path: { project: projectId },
            field: 'ss.overview-url',
          },
          ksStandard: {
            path: { project: projectId },
            field: 'ks.standard.overview-url',
          },
        })}
      />
    </Space>
  );
};
const _ProjectLegalBasesPinpointItem: FC<{
  projectId: string;
  pinpointSheetType: PinpointSheetType;
  legalBases: z.infer<typeof LegalBasisModel>[];
}> = ({ projectId, pinpointSheetType, legalBases }) => {
  const { t } = useTranslation();
  return (
    <Flex vertical gap={16}>
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType,
          rs: {
            path: { project: projectId },
            field: 'rs.legal-bases-title',
          },
          ss: {
            path: { project: projectId },
            field: 'ss.legal-bases-title',
          },
          ksStandard: {
            path: { project: projectId },
            field: 'ks.standard.legal-bases-title',
          },
        })}
      >
        <Text variant="h4" text={t('legal-bases')} />
      </PinpointMarker>

      {legalBases?.length ? (
        <Row gutter={[24, 16]}>
          {legalBases.map((legalBasis) => {
            return (
              <Col span={12} key={legalBasis.id}>
                <PinpointMarker
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType,
                    rs: {
                      path: {
                        project: projectId,
                        legalBasis: legalBasis.name,
                      },
                      field: 'rs.legal-bases',
                    },
                    ss: {
                      path: {
                        project: projectId,
                        legalBasis: legalBasis.name,
                      },
                      field: 'ss.legal-bases',
                    },
                    ksStandard: {
                      path: {
                        project: projectId,
                        legalBasis: legalBasis.name,
                      },
                      field: 'ks.standard.legal-bases',
                    },
                  })}
                >
                  <LegalBasisCard legalBasis={legalBasis} />
                </PinpointMarker>
              </Col>
            );
          })}
        </Row>
      ) : (
        FALLBACK_TEXT
      )}
    </Flex>
  );
};
const _ProjectPlanPinpointItem: FC<{
  projectId: string;
  pinpointSheetType: PinpointSheetType;
  plans: z.infer<typeof PlanModel>[];
}> = ({ projectId, pinpointSheetType, plans }) => {
  const { t } = useTranslation();
  return (
    <Flex vertical gap={16}>
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType,
          rs: {
            path: { project: projectId },
            field: 'rs.plan-title',
          },
          ss: {
            path: { project: projectId },
            field: 'ss.plan-title',
          },
          ksStandard: {
            path: { project: projectId },
            field: 'ks.standard.plan-title',
          },
        })}
      >
        <Text variant="h4" text={t('plan')} />
      </PinpointMarker>
      {plans?.length ? (
        <Row gutter={[24, 16]}>
          {plans.map((plan, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            <Col span={12} key={index}>
              <PinpointMarker
                pinpoint={getPinpointSceneAndFieldBySheetType({
                  pinpointSheetType,
                  rs: {
                    path: { project: projectId, plan: plan.contents || '' },
                    field: 'rs.plan',
                  },
                  ss: {
                    path: { project: projectId, plan: plan.contents || '' },
                    field: 'ss.plan',
                  },
                  ksStandard: {
                    path: { project: projectId, plan: plan.contents || '' },
                    field: 'ks.standard.plan',
                  },
                })}
              >
                <PlanCard plan={plan} />
              </PinpointMarker>
            </Col>
          ))}
        </Row>
      ) : (
        FALLBACK_TEXT
      )}
    </Flex>
  );
};
const _KsCorporateNamePinpointItem: FC<{
  projectId: string;
  pinpointSheetType: Extract<PinpointSheetType, 'KS_INVESTMENT'>;
  corporateName: string | null | undefined;
}> = ({ projectId, pinpointSheetType, corporateName }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        ksInvestment: {
          path: { project: projectId },
          field: 'ks.investment.corporate-name',
        },
      })}
    >
      <TitledText
        key="corporate-name"
        title={t('corporate-name')}
        text={corporateName}
      />
    </PinpointMarker>
  );
};

const _KsOperationFormPinpointItem: FC<{
  projectId: string;
  pinpointSheetType: PinpointSheetType;
  operationForms: z.infer<
    typeof FundAdditionalAttributesModel
  >['operationForms'];
}> = ({ projectId, pinpointSheetType, operationForms }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        ksStandard: {
          field: 'ks.standard.operation-form',
          path: { project: projectId },
        },
        ksLocalGovernment: {
          field: 'ks.local-government.operation-form',
          path: { project: projectId },
        },
      })}
    >
      <TitledText
        title={t('operation-form')}
        text={operationForms
          ?.map((form) => OperationFormLabelModifier[form])
          .join(', ')}
      />
    </PinpointMarker>
  );
};

const _KsBusinessFormPinpointItem: FC<{
  projectId: string;
  pinpointSheetType: PinpointSheetType;
  businessForms: z.infer<typeof FundAdditionalAttributesModel>['businessForms'];
}> = ({ projectId, pinpointSheetType, businessForms }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        ksStandard: {
          field: 'ks.standard.business-form',
          path: { project: projectId },
        },
        ksLocalGovernment: {
          field: 'ks.local-government.business-form',
          path: { project: projectId },
        },
      })}
    >
      <TitledText
        title={t('business-form')}
        text={businessForms
          ?.map((form) => BusinessFormLabelModifier[form])
          .join(', ')}
      />
    </PinpointMarker>
  );
};

const _KsFundNecessityTypePinpointItem: FC<{
  projectId: string;
  pinpointSheetType: PinpointSheetType;
  necessityType: z.infer<typeof FundAdditionalAttributesModel>['necessityType'];
}> = ({ projectId, pinpointSheetType, necessityType }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        ksStandard: {
          field: 'ks.standard.fund-necessity-type',
          path: { project: projectId },
        },
        ksLocalGovernment: {
          field: 'ks.local-government.fund-necessity-type',
          path: { project: projectId },
        },
      })}
    >
      <TitledText
        variant="text"
        title={t('fund-necessity-type')}
        text={necessityType && FundNecessityTypeLabelModifier[necessityType]}
      />
    </PinpointMarker>
  );
};

const _KsFundNecessityReasonsPinpointItem: FC<{
  projectId: string;
  pinpointSheetType: PinpointSheetType;
  necessityReasons: z.infer<
    typeof FundAdditionalAttributesModel
  >['necessityReasons'];
}> = ({ projectId, pinpointSheetType, necessityReasons }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      key="fund-necessity-reasons-for-local-government"
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        ksStandard: {
          field: 'ks.standard.fund-necessity-reasons-for-local-government',
          path: { project: projectId },
        },
        ksLocalGovernment: {
          field:
            'ks.local-government.fund-necessity-reasons-for-local-government',
          path: { project: projectId },
        },
      })}
    >
      <TitledText
        variant="text"
        title={t('fund-necessity-reasons-for-local-government')}
        text={necessityReasons}
      />
    </PinpointMarker>
  );
};

export const ProjectReferencePinpointItem: FC<{
  projectId: string;
  pinpointSheetType: PinpointSheetType;
  projectReference: string | null | undefined;
}> = ({ projectId, pinpointSheetType, projectReference }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.project-reference',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.project-reference',
        },
        ksStandard: {
          path: { project: projectId },
          field: 'ks.standard.project-reference',
        },
        ksLocalGovernment: {
          path: { project: projectId },
          field: 'ks.local-government.project-reference',
        },
        ksInvestment: {
          path: { project: projectId },
          field: 'ks.investment.project-reference',
        },
      })}
    >
      <TitledText
        variant="text"
        title={t('project-reference')}
        text={projectReference}
      />
    </PinpointMarker>
  );
};

export const LegalBasisPinpointItem: FC<{
  projectId: string;
  pinpointSheetType: PinpointSheetType;
  legalBasis: string | null | undefined;
}> = ({ projectId, pinpointSheetType, legalBasis }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.legal-basis',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.legal-basis',
        },
        ksStandard: {
          path: { project: projectId },
          field: 'ks.standard.legal-basis',
        },
        ksLocalGovernment: {
          path: { project: projectId },
          field: 'ks.local-government.legal-basis',
        },
        ksInvestment: {
          path: { project: projectId },
          field: 'ks.investment.legal-basis',
        },
      })}
    >
      <TitledText variant="text" title={t('legal-basis')} text={legalBasis} />
    </PinpointMarker>
  );
};

const _BeneficiaryInfoPinpointItem: FC<{
  projectId: string;
  pinpointSheetType: PinpointSheetType;
  numberOfBeneficiaries: number | null | undefined;
  totalPopulation: number | null | undefined;
}> = ({
  projectId,
  pinpointSheetType,
  numberOfBeneficiaries,
  totalPopulation,
}) => {
  const { t } = useTranslation();

  // 受益者割合の計算
  const beneficiaryRatio = React.useMemo(() => {
    if (!numberOfBeneficiaries || !totalPopulation || totalPopulation === 0) {
      return undefined;
    }
    return ((numberOfBeneficiaries / totalPopulation) * 100).toFixed(2);
  }, [numberOfBeneficiaries, totalPopulation]);

  return (
    <Space direction="vertical">
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType,
          rs: {
            path: { project: projectId },
            field: 'rs.beneficiary-info',
          },
          ss: {
            path: { project: projectId },
            field: 'ss.beneficiary-info',
          },
          ksStandard: {
            path: { project: projectId },
            field: 'ks.standard.beneficiary-info',
          },
          ksLocalGovernment: {
            path: { project: projectId },
            field: 'ks.local-government.beneficiary-info',
          },
          ksInvestment: {
            path: { project: projectId },
            field: 'ks.investment.beneficiary-info',
          },
        })}
      >
        <Row gutter={[24, 0]}>
          <Col span={8}>
            <TitledText
              variant="text"
              title={t('number-of-beneficiaries')}
              text={numberOfBeneficiaries?.toLocaleString()}
            />
          </Col>
          <Col span={8}>
            <TitledText
              variant="text"
              title={t('total-population')}
              text={totalPopulation?.toLocaleString()}
            />
          </Col>
          <Col span={8}>
            <TitledText
              variant="text"
              title={t('beneficiary-ratio')}
              text={beneficiaryRatio ? `${beneficiaryRatio}%` : '-'}
            />
          </Col>
        </Row>
      </PinpointMarker>
    </Space>
  );
};

const _RelatedProjectForOtherMinistryPinpointItem: FC<{
  projectId: string;
  pinpointSheetType: PinpointSheetType;
  relatedProjectForOtherMinistry: string | null | undefined;
}> = ({ projectId, pinpointSheetType, relatedProjectForOtherMinistry }) => {
  const { t } = useTranslation();
  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType,
        rs: {
          path: { project: projectId },
          field: 'rs.related-project-for-other-ministry',
        },
        ss: {
          path: { project: projectId },
          field: 'ss.related-project-for-other-ministry',
        },
        ksStandard: {
          path: { project: projectId },
          field: 'ks.standard.related-project-for-other-ministry',
        },
        ksLocalGovernment: {
          path: { project: projectId },
          field: 'ks.local-government.related-project-for-other-ministry',
        },
        ksInvestment: {
          path: { project: projectId },
          field: 'ks.investment.related-project-for-other-ministry',
        },
      })}
    >
      <TitledText
        variant="text"
        title={t('related-project-for-other-ministry')}
        text={relatedProjectForOtherMinistry}
      />
    </PinpointMarker>
  );
};
